import { useEffect, useState } from 'react';
import { ImageIcon, Loader2, X, Download, RefreshCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session cache so re-mounting (e.g. closing a lightbox) is instant.
const PHOTO_CACHE = new Map(); // id -> blobUrl

// Concurrency limiter — only N photo fetches in flight at any moment.
// Prevents firing 15+ simultaneous requests against the backend.
const MAX_PARALLEL = 4;
let activeFetches = 0;
const waitQueue = [];

function acquireSlot() {
  return new Promise((resolve) => {
    const tryAcquire = () => {
      if (activeFetches < MAX_PARALLEL) {
        activeFetches++;
        resolve();
      } else {
        waitQueue.push(tryAcquire);
      }
    };
    tryAcquire();
  });
}

function releaseSlot() {
  activeFetches--;
  const next = waitQueue.shift();
  if (next) next();
}

export function PhotoThumbnail({ photo, onRemove }) {
  const [blobUrl, setBlobUrl] = useState(() => PHOTO_CACHE.get(photo?.id) || null);
  const [loading, setLoading] = useState(!PHOTO_CACHE.get(photo?.id));
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    if (!photo?.id) return;
    const cached = PHOTO_CACHE.get(photo.id);
    if (cached) {
      setBlobUrl(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let slotAcquired = false;

    setLoading(true);
    setError(null);

    (async () => {
      await acquireSlot();
      slotAcquired = true;
      if (cancelled) { releaseSlot(); return; }
      try {
        const res = await fetch(`${API}/photos/${photo.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        if (!blob || blob.size === 0) throw new Error('Empty response');
        const url = URL.createObjectURL(blob);
        PHOTO_CACHE.set(photo.id, url);
        setBlobUrl(url);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Load failed');
      } finally {
        if (!cancelled) setLoading(false);
        if (slotAcquired) releaseSlot();
      }
    })();

    return () => { cancelled = true; };
  }, [photo?.id, retryTick]);

  const retry = (e) => {
    e.stopPropagation();
    PHOTO_CACHE.delete(photo.id);
    setBlobUrl(null);
    setError(null);
    setRetryTick((t) => t + 1);
  };

  const downloadOriginal = (e) => {
    e.stopPropagation();
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = photo.filename || `photo-${photo.id}.jpg`;
    a.click();
  };

  return (
    <>
      <div
        className="relative group rounded-lg border border-slate-200 bg-slate-50 overflow-hidden cursor-pointer hover:border-teal-400 hover:shadow-md transition"
        onClick={() => !loading && !error && blobUrl && setLightboxOpen(true)}
        data-testid={`photo-thumb-${photo.id}`}
      >
        <div className="aspect-square w-full bg-slate-100 flex items-center justify-center">
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          ) : error || !blobUrl ? (
            <div className="flex flex-col items-center gap-1 text-slate-400 p-2 text-center">
              <ImageIcon className="h-6 w-6" />
              <span className="text-[10px]">Couldn't load</span>
              <button
                type="button"
                onClick={retry}
                className="mt-0.5 text-[10px] inline-flex items-center gap-1 text-teal-600 hover:text-teal-700"
                data-testid={`photo-retry-${photo.id}`}
              >
                <RefreshCcw className="h-3 w-3" /> Retry
              </button>
            </div>
          ) : (
            <img
              src={blobUrl}
              alt={photo.filename || 'Report photo'}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={() => setError('Image format not supported by browser')}
            />
          )}
        </div>
        <div className="px-2 py-1.5 bg-white">
          <p className="text-[11px] font-medium text-slate-700 truncate" title={photo.filename}>
            {photo.filename || 'Photo'}
          </p>
          <p className="text-[10px] text-slate-400">{photo.size ? `${(photo.size / 1024).toFixed(0)} KB` : ''}</p>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10"
            aria-label="Remove photo"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {lightboxOpen && blobUrl && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
          data-testid="photo-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            aria-label="Close"
          >
            <X className="h-7 w-7" />
          </button>
          <button
            type="button"
            onClick={downloadOriginal}
            className="absolute top-4 left-4 text-white/80 hover:text-white inline-flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/20 rounded-md px-3 py-1.5"
          >
            <Download className="h-4 w-4" /> Download
          </button>
          <img
            src={blobUrl}
            alt={photo.filename || 'Report photo'}
            className="max-w-full max-h-full object-contain rounded-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          {photo.filename && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/40 rounded-md px-3 py-1">
              {photo.filename}
            </div>
          )}
        </div>
      )}
    </>
  );
}
